/**
 * OneVersusOnePigMap — Minimal combat testing map for single mob.
 *
 * Single ground platform, exactly one pig mob.
 * Intended for strict combat verification and debugging.
 */

import { PlatformerLevel } from '../../shared/CharacterTypes';
import { MAP_DEFAULTS } from '../../config/map/defaults.config';
import { GridLevel, LayerType } from '../../shared/TileEditorTypes';

const MAP_ID = '1v1-pig';
const MAP_NAME = '1v1 Pig Test';
const VERSION = '1.0';

const W = MAP_DEFAULTS.widthTiles;
const H = MAP_DEFAULTS.heightTiles;
const T = MAP_DEFAULTS.tileSize;

const groundLayer = new Map<string, number>();
const collisionLayer = new Map<string, number>();
const objectsLayer = new Map<string, number>();
const entitiesLayer = new Map<string, number>();
const triggersLayer = new Map<string, number>();

const GROUND_Y = 700;

// pig_mom hitbox height — must match entities.gen.ts pig_mom.height.
// entity.y is the TOP of the hitbox (see GroundCheckSystem.checkGrounded:
// entityBottom = entity.position.y + entity.height; platformTop = platform.y),
// so to plant her on the platform we use y = GROUND_Y - PIG_MOM_HEIGHT.
const PIG_MOM_HEIGHT = 130;

// Exactly one pig in the exact center of the ground plane.
const entities: any[] = [
  { type: 'pig_mom', id: 'verification_pig', x: 600, y: GROUND_Y - PIG_MOM_HEIGHT, properties: { displayName: 'Pig Mom', respawnTime: 5000 } }
];

const spawnPoints = [{ x: 10, y: Math.floor((GROUND_Y - 40) / T) }];
const defaultSpawn = { x: 200, y: GROUND_Y - 40 };

const runtimePlatforms = [
  {
    id: 'ground-floor',
    x: 0,
    y: GROUND_Y,
    width: W * T,
    height: MAP_DEFAULTS.ground.height,
    type: MAP_DEFAULTS.ground.type,
    color: MAP_DEFAULTS.ground.color,
  },
];

export const OneVersusOnePigMap: GridLevel & Partial<PlatformerLevel> = {
  id: MAP_ID,
  name: MAP_NAME,
  version: VERSION,
  widthTiles: W,
  heightTiles: H,
  width: W * T,
  height: H * T,
  tileSize: T,
  backgroundColor: MAP_DEFAULTS.backgroundColor,
  gravity: MAP_DEFAULTS.gravity,
  spawnPoints,
  defaultSpawn,
  layers: [
    { name: LayerType.GROUND, visible: true, locked: false, opacity: 1.0, tiles: groundLayer },
    { name: LayerType.COLLISION, visible: true, locked: false, opacity: 1.0, tiles: collisionLayer },
    { name: LayerType.OBJECTS, visible: true, locked: false, opacity: 1.0, tiles: objectsLayer },
    { name: LayerType.ENTITIES, visible: true, locked: false, opacity: 1.0, tiles: entitiesLayer, entities },
    { name: LayerType.TRIGGERS, visible: true, locked: false, opacity: 1.0, tiles: triggersLayer },
  ],
  platforms: runtimePlatforms,
  entities,
  spawnRules: [],
  type: 'field',
  metadata: {
    author: 'Agent',
    description: '1v1 verification map. 1 pig, 1 platform.',
    tags: ['testing', 'combat', 'verification'],
    // PvE-default — single pig combat, not duel PvP. The "1v1" in the
    // name refers to player-vs-pig hit verification.
    profile: 'pve_default',
  },
};
